import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useAuth } from "@/hooks/auth";
import { getTodayStatus } from "@/Api/timeEntries";
import { cn } from "@/lib/utils";

const STATUS_POLL_MS = 60000;
const TICK_MS = 1000;

type JourneyData = {
  entradaAt: Date;
  inicioIntervaloAt: Date | null;
  fimIntervaloAt: Date | null;
  totalWorkSeconds: number;
  priorSessionsSeconds: number;
  autoBreakSeconds: number;
};

const formatDuration = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hours, minutes, secs].map((n) => String(n).padStart(2, "0")).join(":");
};

// Tempo trabalhado na SESSÃO ABERTA atual até `now` (não inclui ciclos já fechados
// hoje — esses já vêm somados em `priorSessionsSeconds`): soma o que já foi
// trabalhado antes do intervalo com o que já foi trabalhado depois dele (se já
// voltou); enquanto o intervalo está em andamento (bateu Início mas não bateu Fim
// ainda), o cálculo não depende de `now` — o relógio fica pausado naturalmente.
const getCurrentSessionSeconds = (journey: JourneyData, now: Date): number => {
  const { entradaAt, inicioIntervaloAt, fimIntervaloAt } = journey;

  if (!inicioIntervaloAt) {
    return (now.getTime() - entradaAt.getTime()) / 1000;
  }

  const workedBeforeBreak = (inicioIntervaloAt.getTime() - entradaAt.getTime()) / 1000;

  if (!fimIntervaloAt) {
    return workedBeforeBreak;
  }

  const workedAfterBreak = (now.getTime() - fimIntervaloAt.getTime()) / 1000;
  return workedBeforeBreak + workedAfterBreak;
};

export const JourneyTimer = () => {
  const { user } = useAuth();
  const hasEmployee = !!user?.employee;

  const [journey, setJourney] = useState<JourneyData | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!hasEmployee) return;

    const fetchStatus = async () => {
      const { data } = await getTodayStatus();
      const overtime = data?.response?.overtime;

      if (overtime?.entrada_at && typeof overtime?.total_work_seconds === "number") {
        setJourney({
          entradaAt: new Date(overtime.entrada_at),
          inicioIntervaloAt: overtime.inicio_intervalo_at
            ? new Date(overtime.inicio_intervalo_at)
            : null,
          fimIntervaloAt: overtime.fim_intervalo_at
            ? new Date(overtime.fim_intervalo_at)
            : null,
          totalWorkSeconds: overtime.total_work_seconds,
          priorSessionsSeconds: overtime.prior_sessions_seconds ?? 0,
          autoBreakSeconds: overtime.auto_break_seconds ?? 0,
        });
      } else {
        setJourney(null);
      }
    };

    fetchStatus();
    const statusInterval = setInterval(fetchStatus, STATUS_POLL_MS);
    return () => clearInterval(statusInterval);
  }, [hasEmployee]);

  useEffect(() => {
    if (!journey) return;

    const tickInterval = setInterval(() => setNow(new Date()), TICK_MS);
    return () => clearInterval(tickInterval);
  }, [journey]);

  if (!hasEmployee || !journey) return null;

  // Intervalo automático (dia com intervalo no horário e sem batidas de intervalo):
  // descontado uma vez, sem deixar o tempo trabalhado negativo — mesma regra do backend.
  const workedSeconds = Math.max(
    0,
    journey.priorSessionsSeconds +
      getCurrentSessionSeconds(journey, now) -
      journey.autoBreakSeconds
  );
  const remainingSeconds = journey.totalWorkSeconds - workedSeconds;
  const isOvertime = remainingSeconds <= 0;
  const isPaused = !!journey.inicioIntervaloAt && !journey.fimIntervaloAt;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl px-4 py-2 shadow",
        isOvertime
          ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
          : "bg-zinc-50 text-foreground dark:bg-[#2c2c2c]"
      )}
      title={
        isPaused
          ? "Jornada pausada (intervalo)"
          : isOvertime
            ? "Hora extra em andamento"
            : "Tempo restante da jornada"
      }
    >
      <Clock className={cn("size-6", isPaused && "opacity-50")} />
      <div className="flex flex-col leading-none gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">
          {isPaused ? "Intervalo" : isOvertime ? "Hora extra" : "Jornada"}
        </span>
        <span className={cn("font-mono text-xl font-semibold tabular-nums", isPaused && "opacity-50")}>
          {isOvertime ? "+" : ""}
          {formatDuration(Math.abs(remainingSeconds))}
        </span>
      </div>
    </div>
  );
};
